import * as debugJs from "debug";
import { getProp, setProp } from "./any.tools";
import { anyReallyAny } from "./object.tools";

const APP_NAMESPACE = "app:";
const CJS_NAMESPACE = "cjs:";
export const COMPUTE_DEBUG_NAMESPACE_FUNCTION = "computeDebugHandlerNamespace";
export const DEBUG_INSTANCE_NUMBER_PROPERTY = "__debugInstanceNumber";

/**
 * DebugHandler is an extension of debugJs.Debugger
 * @see https://github.com/debug-js/debug 
 * * If you don't see anything from VS Code console, you need to add "console": "integratedTerminal" in your launch.json
 */
export interface DebugHandler extends debugJs.Debugger {

    /**
     * trace function 
     * signature or debugJs.Debugger overridden to allow passing args instead of a single string
     */
    (...args: unknown[]): void;

    /**
     * trace("Starting ${message}")
     * @returns TraceInfo with the message and the start milliseconds, undefined if debug is not enabled
     */
    traceStart(...message: unknown[]): TraceInfo | undefined;

    /**
     * trace(message with trace duration in ms)
     * @returns the duration in ms
     */
    traceFinish(traceInfo: TraceInfo | undefined): number;

    /**
     * trace(error with trace duration in ms)
     * @returns the duration in ms
     */
    traceError(traceInfo: TraceInfo | undefined, ...errorMessage: unknown[]): number;
}

/**
 * @returns a DebugHandler for the provided namespace
 */
export function debugNS(namespace: string): DebugHandler {
    const handler = debugJs.debug(namespace) as DebugHandler;
    handler.traceStart = traceStart.bind(handler);
    handler.traceFinish = traceFinish.bind(handler);
    handler.traceError = traceError.bind(handler);
    return handler;
}

/**
 * @returns a DebugHandler for namespace "cjs:namespace"
 */
export function debugCjs(namespace: string): DebugHandler {
    return debugNS(CJS_NAMESPACE + namespace);
}

/**
 * @returns a DebugHandler for namespace "app:namespace"
 */
export function debugApp(namespace: string): DebugHandler {
    return debugNS(APP_NAMESPACE + namespace);
}

/**
 * Property decorator that installs a DebugHandler for namespace "cjs:namespace"
 *  @param namespace debug namespace
 * if no namespace is provided, will search this[COMPUTE_DEBUG_NAMESPACE_FUNCTION] and call it to get the namespace
 * fallback namespace is the element constructor name
 */
export function DebugCjs(namespace?: string): (classPrototype: anyReallyAny, debugKey: string) => void {
    return Debug(namespace, CJS_NAMESPACE);
}

/**
 * Property decorator that installs a DebugHandler for namespace "app:namespace"
 * @param namespace debug namespace
 * if no namespace is provided, will search this[COMPUTE_DEBUG_NAMESPACE_FUNCTION] and call it to get the namespace
 * fallback namespace is the element constructor name
 */
export function DebugApp(namespace?: string): (classPrototype: anyReallyAny, debugKey: string) => void {
    return Debug(namespace, APP_NAMESPACE);
}

// store next instance number for each namespace
const _nextInstanceNumberByNamespace: Record<string, number> = {};

/**
 * Return next number for namespace and increments the next number for the next instance linked to this namespace
 * if options.store registers an instance number within instance[DEBUG_INSTANCE_NUMBER_PROPERTY] 
 */
export function getDebugInstanceNumber(namespace: string, instance: object, options?: { store: boolean }): number {
    let instanceNumber = getProp<number>(instance, DEBUG_INSTANCE_NUMBER_PROPERTY);
    if (instanceNumber == null) {
        if (_nextInstanceNumberByNamespace[namespace] == null) { _nextInstanceNumberByNamespace[namespace] = 1; }
        instanceNumber = _nextInstanceNumberByNamespace[namespace]++;
        if (options?.store) {
            setProp(instance, DEBUG_INSTANCE_NUMBER_PROPERTY, instanceNumber);
        }
    }
    return instanceNumber;
}

/**
 * Appends instance number to namespace and increments the next number for the next instance linked to this namespace
 * if options.store registers an instance number within instance[DEBUG_INSTANCE_NUMBER_PROPERTY] 
 */
export function appendDebugInstanceNumber(namespace: string, instance: object, options?: { store: boolean }): string {
    return namespace + "." + getDebugInstanceNumber(namespace, instance, options);
}

/**
 * Property decorator that installs a DebugHandler on the debug property
 * This is equivalent to calling debugJs.debug(namespace) on the first access of the debug property
 * @param namespace debug namespace
 * if no namespace is provided, will search this[COMPUTE_DEBUG_NAMESPACE_FUNCTION] and call it to get the namespace
 * fallback namespace is the element constructor name
 * @param prefix optional prefix added before namespace
 */
export function Debug(namespace?: string, prefix?: string): (classPrototype: anyReallyAny, debugKey: string) => void {
    return (classPrototype: anyReallyAny, debugKey: string): void => {
        const internalProperty = "debugHandler__" + debugKey;

        Object.defineProperty(classPrototype, debugKey, {
            get: function () {
                let handler = this[internalProperty] as DebugHandler;
                if (!handler) {
                    let ns: string | undefined;
                    if (namespace != null) {
                        ns = namespace;
                    } else if (this[COMPUTE_DEBUG_NAMESPACE_FUNCTION]) {
                        ns = this[COMPUTE_DEBUG_NAMESPACE_FUNCTION]();
                    }
                    if (ns == null) {
                        ns = appendDebugInstanceNumber(this.constructor.name, this);
                    }
                    this[internalProperty] = handler = debugNS((prefix ?? "") + ns);
                }
                return handler;
            }
        });
    };
}

export interface TraceInfo {
    /** number of milliseconds corresponding to Date.now() when traceStart was called */
    startMs: number;
    /** message corresponding to the current trace */
    message: unknown[];
}

/**
 * Display "Starting ${message}" in log console
 * @returns TraceInfo with the message and the start milliseconds
 */
function traceStart(this: DebugHandler, ...message: unknown[]): TraceInfo | undefined {
    this("Starting", ...message);
    return {
        startMs: Date.now(),
        message
    };
}

/**
 * Display message in log console with trace duration in ms
 * @returns the duration in ms
 */
function traceFinish(this: DebugHandler, traceInfo: TraceInfo | undefined): number {
    if (traceInfo == null) { return 0; }
    const duration = Date.now() - traceInfo.startMs;
    this("[" + duration + " ms]", ...traceInfo.message);
    return duration;
}

/**
 * Display error in log console with trace duration in ms
 * @returns the duration in ms
 */
function traceError(this: DebugHandler, traceInfo: TraceInfo | undefined, ...errorMessage: unknown[]): number {
    if (traceInfo == null) { return 0; }
    const duration = Date.now() - traceInfo.startMs;
    this("[Error after " + duration + " ms]", ...traceInfo.message);
    this(...errorMessage);
    return duration;
}